import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import StepProgressIndicator from '../../components/ui/StepProgressIndicator';
import WorkflowNavigation from '../../components/ui/WorkflowNavigation';
import PDFViewer from './components/PDFViewer';
import ActionPanel from './components/ActionPanel';
import Icon from '../../components/AppIcon';
import Footer from '../../components/Footer';
import { referralService } from '../../services/referralService';
import { practitionerService } from '../../services/practitionerService';
import { generateReferralPDFBlob, blobToBase64 } from '../../utils/pdfGenerator';

const PDFPreview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const [pdfData, setPdfData] = useState(null);
  const [practitionerData, setPractitionerData] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [referralId, setReferralId] = useState(null);
  const [publicToken, setPublicToken] = useState(null);
  const [isLoadingPractitioner, setIsLoadingPractitioner] = useState(false);

  // Initialize pdfData from navigation state
  useEffect(() => {
    const referralData = location?.state?.referralData;
    if (referralData) {
      setPdfData(referralData);
      console.log('✅ PDF data loaded from navigation state:', referralData);
    } else {
      console.warn('⚠️ No referralData found in navigation state');
    }
  }, [location?.state?.referralData]);

  // Fetch fresh practitioner data from database to ensure latest visual identity settings
  useEffect(() => {
    const fetchPractitionerData = async () => {
      // If slug exists, fetch fresh data from database
      if (slug) {
        try {
          setIsLoadingPractitioner(true);
          const freshPractitionerData = await practitionerService?.getBySlug(slug);
          
          if (freshPractitionerData) {
            setPractitionerData(freshPractitionerData);
            console.log('✅ Fresh practitioner data loaded:', {
              logoUrl: freshPractitionerData?.logoUrl,
              logoPosition: freshPractitionerData?.logoPosition,
              logoSize: freshPractitionerData?.logoSize,
              signatureUrl: freshPractitionerData?.signatureUrl
            });
          }
        } catch (error) {
          console.error('Error fetching practitioner data:', error);
          // Fallback to state data if fetch fails
          const practitioner = location?.state?.practitionerData;
          if (practitioner) {
            setPractitionerData(practitioner);
          }
        } finally {
          setIsLoadingPractitioner(false);
        }
      } else {
        // No slug, use data from navigation state
        const practitioner = location?.state?.practitionerData;
        if (practitioner) {
          setPractitionerData(practitioner);
        }
      }
    };

    fetchPractitionerData();
  }, [slug, location?.state?.practitionerData]);

  const handleEdit = () => {
    // Navigate back to practitioner form if slug exists
    const targetRoute = slug ? `/p/${slug}` : '/referral-form';
    navigate(targetRoute, { state: { formData: pdfData } });
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    const blob = new Blob(['Contenu PDF généré ici'], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `adressage-endodontie-${Date.now()}.pdf`;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async () => {
    setIsGenerating(true);
    setSaveError(null);

    try {
      // CRITICAL: Validate all required data before submission
      if (!pdfData) {
        throw new Error('Données du formulaire manquantes');
      }
      
      if (!practitionerData) {
        throw new Error('Données du praticien manquantes');
      }
      
      if (!practitionerData?.slug) {
        throw new Error('Slug du praticien manquant');
      }

      // STEP 1: Create referral draft via Edge Function (returns referral_id + public_token)
      console.log('🚀 Step 1: Creating referral draft...');
      const createResult = await referralService?.create({
        ...pdfData,
        practitionerSlug: practitionerData?.slug
      });

      if (!createResult?.id || !createResult?.publicToken) {
        throw new Error('Échec de la création de l\'adressage');
      }

      console.log('✅ Step 1 complete: Draft created with ID:', createResult?.id);
      
      // Store for finalization
      setReferralId(createResult?.id);
      setPublicToken(createResult?.publicToken);

      // STEP 1.5: Generate PDF blob and convert to base64
      console.log('🚀 Step 1.5: Generating PDF blob...');
      const pdfBlob = await generateReferralPDFBlob(pdfData, practitionerData);
      console.log('✅ PDF blob generated, size:', pdfBlob?.size, 'bytes');
      
      // Convert blob to base64 (without data URI prefix)
      console.log('🚀 Converting PDF blob to base64...');
      const base64Pdf = await blobToBase64(pdfBlob);
      console.log('✅ PDF converted to base64, length:', base64Pdf?.length, 'characters');

      // STEP 2: Finalize referral (update status to 'new' and send PDF)
      console.log('🚀 Step 2: Finalizing referral with PDF...');
      console.log('📍 Calling finalize-referral with:');
      console.log('   - referral_id:', createResult?.id);
      console.log('   - public_token:', createResult?.publicToken?.substring(0, 8) + '...');
      console.log('   - pdf_blob: provided (base64, length:', base64Pdf?.length, ')');
      
      const finalizeResult = await referralService?.finalize(
        createResult?.id,
        createResult?.publicToken,
        base64Pdf // Pass base64 PDF string
      );

      if (!finalizeResult?.success) {
        throw new Error('Échec de la finalisation de l\'adressage');
      }

      console.log('✅ Step 2 complete: Referral finalized with PDF');
      
      // Clear form draft from localStorage after successful save
      localStorage.removeItem('referralFormDraft');

      // Navigate to confirmation with complete data
      const targetRoute = slug 
        ? `/p/${slug}/confirmation`
        : '/submission-confirmation';
      
      navigate(targetRoute, {
        state: {
          pdfData,
          practitionerData,
          referralId: createResult?.id,
          savedReferral: {
            id: createResult?.id,
            status: 'new'
          }
        }
      });
    } catch (error) {
      console.error('❌ Submission error:', error);
      setSaveError(error?.message || 'Erreur lors de l\'enregistrement de l\'adressage');
      setIsGenerating(false);
    }
  };

  const handlePrevious = () => {
    navigate('/referral-form', { state: { formData: pdfData } });
  };

  const handleNext = () => {
    handleSubmit();
  };

  return (
    <>
      <Helmet>
        <title>{practitionerData?.practitionerName ? `Aperçu PDF - ${practitionerData?.practitionerTitle} ${practitionerData?.practitionerName}` : 'Aperçu PDF - Dr Arnaud Touati'}</title>
        <meta name="description" content="Aperçu de votre document d'adressage endodontique" />
      </Helmet>
      <div className="pdf-preview-page">
        {/* Parallax background layer */}
        <div className="parallax-background" />
        
        <div className="brand-header-preview">
          <div className="header-content-wrapper-preview">
            <div className="at-logo-monogram-preview">
              {practitionerData?.logoInitials || practitionerData?.practitionerName?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase() || 'XX'}
            </div>
            <div className="header-text-content-preview">
              <h1 className="brand-title-preview">
                {practitionerData?.practitionerName 
                  ? `${practitionerData?.practitionerTitle || 'Dr'} ${practitionerData?.practitionerName}` 
                  : 'Praticien'}
              </h1>
              <p className="brand-subtitle-preview">
                {practitionerData?.practiceName || 'Endodontie exclusive'}
              </p>
            </div>
          </div>
        </div>
        <StepProgressIndicator
          currentStep={2}
          totalSteps={3}
          stepLabels={['Formulaire', 'Aperçu PDF', 'Confirmation']}
          completedSteps={[1]}
        />

        <div className="preview-container">
          <div className="pdf-preview-header fade-in-up">
            <div className="header-content-inner">
              <Icon name="FileText" size={32} className="text-primary" />
              <div className="header-text">
                <h1 className="text-3xl md:text-4xl font-semibold text-foreground">Vérifier le document</h1>
                <p className="text-sm md:text-base text-muted-foreground">
                  Vérifiez attentivement le PDF généré avant de finaliser votre demande d'adressage
                </p>
              </div>
            </div>
          </div>

          {saveError && (
            <div className="error-banner fade-in-up">
              <Icon name="AlertCircle" size={20} className="text-red-400" />
              <div>
                <p className="font-semibold text-red-400">Erreur d'enregistrement</p>
                <p className="text-sm text-red-300">{saveError}</p>
              </div>
            </div>
          )}

          <div className="pdf-preview-content">
            <div className="pdf-viewer-section">
              <PDFViewer
                pdfData={pdfData}
                practitionerData={practitionerData}
                onEdit={handleEdit}
                onPrint={handlePrint}
                onDownload={handleDownload}
              />
            </div>

            <aside className="action-panel-section">
              <ActionPanel
                pdfData={pdfData}
                practitionerData={practitionerData}
                onEdit={handleEdit}
                onPrint={handlePrint}
                onDownload={handleDownload}
                onSubmit={handleSubmit}
                isLoading={isGenerating}
              />

              <div className="info-card">
                <div className="info-card-header">
                  <Icon name="Info" size={20} className="text-primary" />
                  <h4 className="font-semibold text-foreground">Informations du document</h4>
                </div>
                <div className="info-card-content">
                  <div className="info-row">
                    <span className="caption text-muted-foreground">Généré</span>
                    <span className="caption font-medium text-foreground">
                      {new Date()?.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="caption text-muted-foreground">Format</span>
                    <span className="caption font-medium text-foreground">Document PDF</span>
                  </div>
                  <div className="info-row">
                    <span className="caption text-muted-foreground">Statut</span>
                    <span className="status-badge">
                      <Icon name="Clock" size={14} />
                      En attente de vérification
                    </span>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>

        <WorkflowNavigation
          currentStep={2}
          totalSteps={3}
          onNext={handleNext}
          onPrevious={handlePrevious}
          isNextDisabled={isGenerating}
          isLoading={isGenerating}
          nextLabel="Finaliser l'adressage"
          previousLabel="Modifier le formulaire"
          showCancel={false}
          onCancel={() => {}}
        />

        <Footer practitionerData={practitionerData} />

        <style jsx>{`
          .pdf-preview-page {
            min-height: 100vh;
            background: #1A1A1E;
            display: flex;
            flex-direction: column;
            position: relative;
            overflow-x: hidden;
          }

          /* Subtle parallax background */
          .parallax-background {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: radial-gradient(ellipse at 50% 0%, rgba(74, 144, 226, 0.08) 0%, transparent 50%),
                        radial-gradient(ellipse at 80% 100%, rgba(74, 144, 226, 0.05) 0%, transparent 50%);
            z-index: 0;
            pointer-events: none;
          }

          .brand-header-preview {
            background: rgba(15, 15, 15, 0.8);
            backdrop-filter: blur(40px) saturate(180%);
            -webkit-backdrop-filter: blur(40px) saturate(180%);
            border-bottom: 1px solid rgba(255, 255, 255, 0.06);
            padding: 2rem 1rem;
            position: relative;
            z-index: 10;
            flex-shrink: 0;
          }

          .header-content-wrapper-preview {
            max-width: 1280px;
            margin: 0 auto;
            display: flex;
            align-items: center;
            gap: 1.5rem;
            position: relative;
          }

          .at-logo-monogram-preview {
            width: 64px;
            height: 64px;
            background: rgba(22, 22, 26, 0.6);
            backdrop-filter: blur(20px);
            color: #F5F5F7;
            border: 1px solid rgba(74, 144, 226, 0.2);
            border-radius: 16px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.75rem;
            font-weight: 700;
            letter-spacing: 0.05em;
            flex-shrink: 0;
            box-shadow: 
              0 8px 32px rgba(0, 0, 0, 0.6),
              0 0 20px rgba(74, 144, 226, 0.1),
              0 0 0 1px rgba(255, 255, 255, 0.03) inset;
          }

          .header-text-content-preview {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
          }

          .brand-title-preview {
            font-size: 1.75rem;
            font-weight: 600;
            color: #F5F5F7;
            margin: 0;
            letter-spacing: -0.03em;
          }

          .brand-subtitle-preview {
            font-size: 0.95rem;
            color: #98989D;
            margin: 0;
            letter-spacing: 0.01em;
            font-weight: 400;
          }

          .preview-container {
            flex: 1;
            display: flex;
            flex-direction: column;
            max-width: 1400px;
            width: 100%;
            margin: 0 auto;
            padding: 3rem 1rem;
            gap: 2rem;
            position: relative;
            z-index: 1;
          }

          .pdf-preview-header {
            text-align: center;
            margin-bottom: 2rem;
          }

          .header-content-inner {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 1rem;
          }

          .header-text {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
          }

          .error-banner {
            display: flex;
            align-items: flex-start;
            gap: 1rem;
            padding: 1rem 1.5rem;
            background: rgba(220, 38, 38, 0.1);
            border: 1px solid rgba(220, 38, 38, 0.3);
            border-radius: 12px;
            margin-bottom: 1.5rem;
          }

          .pdf-preview-content {
            display: grid;
            grid-template-columns: 1fr;
            gap: 2rem;
          }

          @media (min-width: 1024px) {
            .pdf-preview-content {
              grid-template-columns: 1fr 320px;
            }
          }

          .pdf-viewer-section {
            min-height: 600px;
            background: rgba(22, 22, 22, 0.4);
            backdrop-filter: blur(40px) saturate(180%);
            -webkit-backdrop-filter: blur(40px) saturate(180%);
            border-radius: 20px;
            overflow: hidden;
            border: 1px solid rgba(255, 255, 255, 0.08);
            box-shadow: 
              0 8px 32px rgba(0, 0, 0, 0.5),
              0 0 0 1px rgba(255, 255, 255, 0.03) inset;
          }

          .action-panel-section {
            display: flex;
            flex-direction: column;
            gap: 1.5rem;
          }

          .info-card {
            display: flex;
            flex-direction: column;
            gap: 1rem;
            padding: 1.5rem;
            background: rgba(22, 22, 22, 0.4);
            backdrop-filter: blur(40px) saturate(180%);
            -webkit-backdrop-filter: blur(40px) saturate(180%);
            border-radius: 20px;
            border: 1px solid rgba(255, 255, 255, 0.08);
            box-shadow: 
              0 8px 32px rgba(0, 0, 0, 0.5),
              0 0 0 1px rgba(255, 255, 255, 0.03) inset;
          }

          .info-card-header {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding-bottom: 1rem;
            border-bottom: 1px solid rgba(255, 255, 255, 0.06);
          }

          .info-card-content {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
          }

          .info-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .status-badge {
            display: inline-flex;
            align-items: center;
            gap: 0.375rem;
            padding: 0.375rem 0.875rem;
            background: rgba(245, 158, 11, 0.12);
            color: #F59E0B;
            border-radius: 10px;
            font-size: 0.8125rem;
            font-weight: 500;
            border: 1px solid rgba(245, 158, 11, 0.3);
            backdrop-filter: blur(20px);
          }

          @media (max-width: 1280px) {
            .preview-container {
              gap: 1rem;
              padding: 1rem;
            }
          }

          @media (max-width: 1024px) {
            .preview-container {
              gap: 1rem;
            }

            .action-panel-section {
              order: -1;
            }

            .pdf-viewer-section {
              min-height: 500px;
            }
          }

          @media (max-width: 768px) {
            .brand-header-preview {
              padding: 1.5rem 1rem;
            }

            .at-logo-monogram-preview {
              width: 52px;
              height: 52px;
              font-size: 1.5rem;
              border-radius: 12px;
            }

            .brand-title-preview {
              font-size: 1.5rem;
            }

            .brand-subtitle-preview {
              font-size: 0.875rem;
            }

            .preview-container {
              padding: 2rem 1rem;
            }
          }

          @media (max-width: 640px) {
            .brand-header-preview {
              padding: 1rem;
            }

            .info-card {
              padding: 1rem;
            }
          }
        `}</style>
      </div>
    </>
  );
};

export default PDFPreview;